import React, { useState, useEffect } from 'react';
import { fetchLeaderboard, fetchCurrentUserLeaderboardEntry } from '../../services/crimeGameService';
import { GameLeaderboardEntry } from '../../types/crime-game';
import { Trophy, Calendar, Globe } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface LeaderboardPanelProps {
  dateKey?: string;
}

export const LeaderboardPanel: React.FC<LeaderboardPanelProps> = ({ dateKey }) => {
  const [activeTab, setActiveTab] = useState<'daily' | 'global'>('daily');
  const [data, setData] = useState<GameLeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const res = await fetchLeaderboard(activeTab, dateKey);
      
      // Fetch current user's entry and include it if not already in the list
      if (currentUser?.uid) {
        const currentUserEntry = await fetchCurrentUserLeaderboardEntry(
          currentUser.uid,
          activeTab,
          dateKey
        );
        
        if (currentUserEntry) {
          // Check if user is already in the top 10
          const isAlreadyIncluded = res.some(entry => entry.uid === currentUser.uid);
          
          if (!isAlreadyIncluded) {
            // Add current user's entry to the list
            res.push(currentUserEntry);
            // Re-sort to maintain order
            res.sort((a, b) => b.points - a.points);
          }
        }
      }
      
      setData(res);
      setLoading(false);
    };
    load();
  }, [activeTab, dateKey, currentUser?.uid]);

  return (
    <div className="w-full bg-white rounded-xl shadow-md overflow-hidden mt-8">
      <div className="flex border-b">
        <button
          onClick={() => setActiveTab('daily')}
          className={`flex-1 py-4 text-center font-bold flex items-center justify-center space-x-2 transition-colors
            ${activeTab === 'daily'
              ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
              : 'text-gray-500 hover:bg-gray-50'
            }`}
        >
          <Calendar className="w-4 h-4" />
          <span>Daily Top</span>
        </button>
        <button
          onClick={() => setActiveTab('global')}
          className={`flex-1 py-4 text-center font-bold flex items-center justify-center space-x-2 transition-colors
            ${activeTab === 'global'
              ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
              : 'text-gray-500 hover:bg-gray-50'
            }`}
        >
          <Globe className="w-4 h-4" />
          <span>All Time</span>
        </button>
      </div>

      <div className="p-4">
         {loading ? (
           <div className="text-center py-8 text-gray-400">Loading rankings...</div>
         ) : data.length === 0 ? (
           <div className="text-center py-8 text-gray-400">No records found. Be the first!</div>
         ) : (
           <ul className="space-y-2">
             {data.map((entry, idx) => {
               const isMe = currentUser?.uid === entry.uid;
               return (
                 <li
                   key={entry.uid}
                   className={`flex items-center p-3 rounded-lg ${isMe ? 'bg-yellow-50 border border-yellow-200' : 'bg-white border border-gray-100'}`}
                 >
                   <div className="w-8 font-bold text-gray-400 text-center">#{idx + 1}</div>
                   <div className="flex-1 px-3">
                     <div className="font-bold text-gray-800 flex items-center">
                       {entry.displayName || 'Anonymous Detective'}
                       {isMe && <span className="ml-2 text-xs bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">You</span>}
                     </div>
                     <div className="text-xs text-gray-500">
                       Wins: {entry.wins} | Streak: {entry.streak}
                     </div>
                   </div>
                   <div className="font-mono font-bold text-indigo-600">
                     {entry.points} pts
                   </div>
                 </li>
               );
             })}
           </ul>
         )}
      </div>
    </div>
  );
};
